/**
 * Nombres de herramientas y vías de captación. Módulo sin dependencias
 * (formTelemetry y publicActivity lo importan sin crear un ciclo).
 */

export type NormTool = "calculadora" | "detector_fugas" | "diagnostico" | "quiz" | "micro_diagnostico";

/** Nombre único de herramienta (acepta las claves antiguas). */
export function normTool(tool: string | null | undefined): NormTool | null {
  const t = (tool ?? "").toLowerCase();
  if (t === "calculadora") return "calculadora";
  if (t.startsWith("detector_fugas") || t === "termometro") return "detector_fugas";
  if (t === "diagnostico" || t === "diagnostico_locales" || t === "locales") return "diagnostico";
  if (t === "quiz" || t === "quiz_report" || t.startsWith("quiz_")) return "quiz";
  if (t === "micro_diagnostico") return "micro_diagnostico";
  return null;
}

export type ToolVariant = "express" | "completo" | "inquilino" | "propietario";

const VARIANTS = new Set<string>(["express", "completo", "inquilino", "propietario"]);

/**
 * Variante deducida de la clave original o de los metadatos (filas antiguas incluidas).
 * Detector: express/completo. Mis locales: inquilino/propietario (tool_variant o role).
 */
export function toolVariantOf(rawKey: string | null | undefined, meta?: Record<string, unknown> | null): ToolVariant | null {
  const m = meta ?? {};
  const explicit = String(m["tool_variant"] ?? m["role"] ?? "").toLowerCase();
  if (VARIANTS.has(explicit)) return explicit as ToolVariant;
  if (explicit === "full") return "completo";
  const k = (rawKey ?? "").toLowerCase();
  if (k === "detector_fugas_completo") return "completo";
  if (normTool(k) === "detector_fugas") {
    if ("full" in m || "full_unlock" in m || "full_club_pro" in m || "full_completed" in m) return "completo";
    if ("express" in m || "express_unlock" in m || "express_club_pro" in m || "express_completed" in m) return "express";
    if (k === "detector_fugas") return "express";
  }
  return null;
}

/** Las 7 vías de captación (valores de waitlist_source y de la cookie rl_channel). */
export const CAPTURE_CHANNELS = [
  "calculadora",
  "diagnostico_inquilino",
  "diagnostico_propietario",
  "detector_express",
  "detector_completo",
  "lp-instagram",
  "test-supervivencia",
] as const;
export type CaptureChannel = (typeof CAPTURE_CHANNELS)[number];

export const isCaptureChannel = (v: string | null | undefined): v is CaptureChannel =>
  !!v && (CAPTURE_CHANNELS as readonly string[]).includes(v);

/** Vía de captación de un evento de herramienta o de landing; null si no es una de las 7. */
export function channelOf(rawKey: string | null | undefined, meta?: Record<string, unknown> | null): CaptureChannel | null {
  const k = (rawKey ?? "").toLowerCase();
  if (k === "lp-instagram" || k === "test-supervivencia") return k;
  const tool = normTool(k);
  const v = toolVariantOf(k, meta);
  if (tool === "calculadora") return "calculadora";
  if (tool === "detector_fugas") return v === "completo" ? "detector_completo" : v === "express" ? "detector_express" : null;
  if (tool === "diagnostico") return v === "propietario" ? "diagnostico_propietario" : v === "inquilino" ? "diagnostico_inquilino" : null;
  return null;
}
